import { useState } from 'react'
import { Link } from 'react-router-dom'
import BookingModal from '../../components/BookingModal'
import FontAwesomeIcon from '../../components/FontAwesomeIcon'

export default function MgahingaBatwaCulture() {
  const [isModalOpen, setIsModalOpen] = useState(false)

  return (
    <div>
      {/* Hero Section */}
      <section
        className="relative h-96 md:h-[500px] bg-cover bg-center flex items-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(https://images.unsplash.com/photo-1516426122078-c23e76319801?w=1600)',
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-4xl text-white">
            <h1 className="text-3xl md:text-5xl font-bold mb-3 font-heading">
              Mgahinga Batwa Cultural Experience
            </h1>
            <p className="text-xl md:text-2xl mb-6">
              Walk with mountain forest people and enter the sacred Garama Cave
            </p>
            <div className="flex items-center gap-6 mb-6">
              <div className="flex items-center gap-2">
                <div className="flex text-safari-gold">
                  {[...Array(5)].map((_, i) => (
                    <FontAwesomeIcon key={i} icon="fa-star" className="w-5 h-5" />
                  ))}
                </div>
                <span className="text-lg">(118 reviews)</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-4 mb-8">
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-home" className="w-4 h-4 mr-2" />
                Garama Sacred Cave
              </div>
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-users" className="w-4 h-4 mr-2" />
                Indigenous Guides
              </div>
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-heart" className="w-4 h-4 mr-2" />
                Community Support
              </div>
            </div>
            <button
              onClick={() => setIsModalOpen(true)}
              className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Book Cultural Journey
            </button>
          </div>
        </div>
      </section>

      {/* Page Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="lg:grid lg:grid-cols-3 lg:gap-12">
          {/* Main Content */}
          <main className="lg:col-span-2">
            {/* Description Section */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8">
                Enter the Sacred Cave of the Forest People
              </h2>
              <div className="prose prose-lg max-w-none">
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  The Batwa cultural experience in Mgahinga Gorilla National Park offers something profoundly different from Bwindi's Batwa Trail—here, the journey culminates at Garama Cave, a massive rock shelter that served as the Batwa king's council chamber and refuge for centuries. This 342-meter-long, 14-meter-high cave holds deep spiritual significance for the Batwa people, who still consider it sacred ground where their ancestors gathered, made decisions that affected the entire community, and hid from threats.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Walking through Mgahinga's bamboo forest with Batwa guides who share hunting techniques, medicinal plant knowledge, and survival skills is fascinating. But when you enter Garama Cave and hear traditional songs echoing off ancient stone walls—songs of celebration, mourning, and spiritual connection—the experience transcends education and becomes genuinely moving. This is living cultural preservation, where displaced forest people maintain identity and pass knowledge to younger generations while sharing their story with visitors whose fees directly support Batwa community development.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Your experience begins at the park entrance where Batwa guides greet you, often dressed in traditional bark cloth. Unlike generic cultural shows, these are actual Batwa community members—many older guides remember living in the forest before eviction when Mgahinga became a national park in 1991. Their knowledge is not reconstructed from books but lived experience passed down through generations.
                </p>
              </div>
            </section>

            {/* Highlights */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-12 text-center">
                Experience Highlights
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                {[
                  { icon: 'fa-home', title: 'Sacred Garama Cave', description: '342m-long ancient council chamber and refuge—spiritual heart of Batwa culture' },
                  { icon: 'fa-music', title: 'Traditional Songs', description: 'Haunting harmonies echo off stone walls—profoundly moving acoustic experience' },
                  { icon: 'fa-users', title: 'Authentic Guides', description: 'Led by actual community members who remember forest life before displacement' },
                  { icon: 'fa-leaf', title: 'Medicinal Plants', description: 'Learn traditional healing practices passed down through millennia' },
                  { icon: 'fa-bullseye', title: 'Hunting Demos', description: 'See bow-and-arrow crafting, tracking skills, and shooting accuracy' },
                  { icon: 'fa-book-open', title: 'Oral History', description: 'Personal narratives of forest life, displacement, and cultural survival' }
                ].map((highlight, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center">
                        <FontAwesomeIcon icon={highlight.icon} className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-safari-green mb-2">{highlight.title}</h3>
                      <p className="text-gray-700">{highlight.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Itinerary */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                Sample Cultural Experience
              </h2>
              <div className="bg-safari-light border-l-4 border-safari-gold p-6 mb-8 rounded-r-lg">
                <p className="text-gray-700 text-lg">
                  <strong>Note:</strong> This itinerary is tentative and can be customized based on your preferences.
                </p>
              </div>
              <div className="space-y-6">
                {[
                  { time: '09:00', title: 'Meet Batwa Guides', desc: 'Park entrance introductions, cultural context, begin forest trek.' },
                  { time: '09:30', title: 'Plant Medicine Walk', desc: 'Learn to identify medicinal plants and traditional uses as you hike.' },
                  { time: '10:15', title: 'Hunting Skills Demo', desc: 'Bow-and-arrow crafting, tracking techniques, shooting demonstration.' },
                  { time: '11:00', title: 'Enter Sacred Cave', desc: 'Explore 342m cave, learn historical uses, hear displacement stories.' },
                  { time: '11:30', title: 'Traditional Songs', desc: 'Batwa harmonies echo through cave—emotional cultural pinnacle.' },
                  { time: '12:15', title: 'Return Trek', desc: 'Hike back through forest with continued cultural exchange.' }
                ].map((step, index) => (
                  <div key={index} className="card">
                    <div className="p-6 flex gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-16 h-16 bg-safari-green rounded-full flex items-center justify-center text-white font-bold text-sm text-center leading-tight p-1">
                          {step.time}
                        </div>
                      </div>
                      <div className="flex-grow">
                        <h3 className="text-xl font-bold text-safari-green mb-2">{step.title}</h3>
                        <p className="text-gray-700">{step.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* FAQs */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-12 text-center">
                Common Questions
              </h2>
              <div className="space-y-6">
                {[
                  { q: 'How does this differ from Bwindi Batwa Trail?', a: 'Both are excellent. Mgahinga centers on Garama Cave—a spectacular sacred space with powerful acoustic performances. Bwindi has no cave but offers longer forest immersion.' },
                  { q: 'Is photography allowed?', a: 'Yes, but always ask permission before photographing individuals. Flash photography in the cave should be avoided as it disrupts the atmosphere.' },
                  { q: 'Do guides speak English?', a: 'Most Batwa guides speak some English, and interpreters are available for complex cultural concepts and stories.' },
                  { q: 'How physically demanding is it?', a: 'Moderate fitness required. The 90-minute forest hike each way is steady but not extreme, on mostly gentle slopes.' }
                ].map((faq, index) => (
                  <div key={index} className="border-l-4 border-safari-gold pl-6 py-4">
                    <h3 className="text-xl font-bold text-safari-green mb-2">{faq.q}</h3>
                    <p className="text-gray-700">{faq.a}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Related Activities */}
            <section>
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                More Mgahinga Adventures
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <Link to="/activities/golden-monkey-tracking" className="card group hover:shadow-xl transition-all duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1540573133985-87b6da6d54a9?w=800"
                      alt="Golden Monkey Tracking"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-safari-green mb-2">Golden Monkey Tracking</h3>
                    <p className="text-gray-700 mb-4">Track rare primates in the bamboo forest.</p>
                    <p className="text-safari-gold font-bold">Activity fees apply</p>
                  </div>
                </Link>

                <Link to="/activities/mount-sabinyo-climb" className="card group hover:shadow-xl transition-all duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=800"
                      alt="Mount Sabinyo"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-safari-green mb-2">Mount Sabinyo Climb</h3>
                    <p className="text-gray-700 mb-4">Summit three countries at once.</p>
                    <p className="text-safari-gold font-bold">Activity fees apply</p>
                  </div>
                </Link>
              </div>
            </section>
          </main>

          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <div className="sticky top-24">
              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-4">Activity Details</h3>
                <div className="space-y-4 text-gray-700">
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-clock" className="w-6 h-6 text-safari-green" />
                    <span><strong>Duration:</strong> 4-5 hours</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-calendar" className="w-6 h-6 text-safari-green" />
                    <span><strong>Best Time:</strong> Year-round</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-dollar-sign" className="w-6 h-6 text-safari-green" />
                    <span><strong>Activity Fee:</strong> Contact for current rates</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-chart-simple" className="w-6 h-6 text-safari-green" />
                    <span><strong>Difficulty:</strong> Moderate</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-users" className="w-6 h-6 text-safari-green" />
                    <span><strong>Group:</strong> Small groups</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-user" className="w-6 h-6 text-safari-green" />
                    <span><strong>Min Age:</strong> 8 years</span>
                  </div>
                </div>
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl w-full mt-6"
                >
                  Book Experience
                </button>
              </div>

              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-check-circle" className="w-6 h-6 text-green-600" />
                  What's Included
                </h3>
                <ul className="space-y-3">
                  {[
                    "Park entrance fees",
                    "Batwa guides",
                    "Garama Cave visit",
                    "Cultural performances",
                    "Community contribution"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-times-circle" className="w-6 h-6 text-red-600" />
                  What's Not Included
                </h3>
                <ul className="space-y-3">
                  {[
                    "Transport to park",
                    "Meals and drinks",
                    "Tips for guides",
                    "Personal insurance"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <FontAwesomeIcon icon="fa-times" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="card p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-heartbeat" className="w-6 h-6" />
                  Health & Safety
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Fitness:</strong> Moderate hiking required.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Terrain:</strong> Uneven forest paths.</span>
                  </li>
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <BookingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        experience="Mgahinga Batwa Cultural Experience"
      />
    </div>
  )
}
